import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'panamera-react-ui';

import { categorySelector, itemsMetadataSelector } from 'Selectors/items';
import { selectedLocationSelector } from 'Selectors/location';

import { withConfig } from 'HOCs/withConfig/withConfig';

import { buildURL } from 'Helpers/url';

import css from './StructuredFooterInterlinks.scss';
import { DEFAULT_TOP_LOCATIONS, config } from './constant';

function getNestedLocationData(locationData, selectedLocation, locationMapping, config) {
    if (
        (
            locationData?.location_type === selectedLocation?.type
            || locationData?.location_type === locationMapping[selectedLocation?.type]
        )
        && locationData?.location_id?.toString() === selectedLocation?.id?.toString()
    ) {
        return locationData?.children?.slice(0, config?.nestedLocation?.topLocations || DEFAULT_TOP_LOCATIONS) || [selectedLocation];
    }
    else if (config?.siblings && locationData?.id === `${selectedLocation?.parentId}`) {
        const nearbyLocations = locationData?.children?.filter(loc => loc?.id?.toString() !== selectedLocation?.id?.toString()) || [];

        return nearbyLocations?.slice(0, config?.nestedLocation?.topLocations || DEFAULT_TOP_LOCATIONS) || [selectedLocation];
    }

    return locationData?.id !== selectedLocation?.id && locationData?.children?.length > 0
        ? getNestedLocationData(locationData?.children[0] || {}, selectedLocation, locationMapping, config)
        : [selectedLocation];
}

export const StructuredFooterInterlinks = React.memo(({
    appliedFilters,
    category,
    filters,
    locationDataFromFilter,
    locationMapping,
    selectedLocation
}) => {
    const selectedLocationType = selectedLocation?.type;
    const categoryConfig = config?.find(config => config.categoryId === category?.id);
    const categoryFilters = categoryConfig?.filters || [];
    const interLinks = [];

    for (const filter of categoryFilters) {
        const locationConfig = filter?.location?.[selectedLocationType];

        if (locationConfig?.enabled) {
            const filterData = filters?.find(fd => fd?.id === filter?.id);
            const filterValues = filterData?.values || [];
            const shouldShowNestedLocation = locationConfig?.nestedLocation?.enabled || selectedLocationType === 'NEIGHBOURHOOD' && locationConfig?.enabled;
            const nestedLocationData = getNestedLocationData(locationDataFromFilter?.[0], selectedLocation, locationMapping, locationConfig);
            const appliedFilter = appliedFilters?.find(af => af?.id === filterData?.id);

            if (locationConfig?.showSelectedLocation && filterValues?.length > 0) {
                let heading = filter?.heading;
                let filterCollection = filterValues.map(flt => ({
                    id: filter?.id,
                    text: filter?.text?.replace('{value}', flt.name)?.replace('{location}', selectedLocation.name),
                    url: buildURL({ category, location: selectedLocation, params: { filter: { [filter.id]: flt.value }}})
                }));

                if (locationConfig?.showOnFilterSelection && appliedFilter?.values?.length === 1) {
                    heading = filter?.heading?.replace('{value}', appliedFilter?.values?.[0]?.name);
                    filterCollection = appliedFilter?.values?.map(flt => ({
                        id: filter?.id,
                        text: filter?.text?.replace('{value}', flt.name)?.replace('{location}', selectedLocation.name),
                        url: buildURL({ category, location: selectedLocation, params: { filter: { [filter.id]: flt.value }}})
                    }));
                }

                interLinks.push({ heading, filters: filterCollection });
            }

            if (!locationConfig?.showSelectedLocation) {
                interLinks.push({ heading: filter?.heading });
            }

            if (shouldShowNestedLocation && filterValues?.length > 0 && nestedLocationData.length > 0) {
                filterValues?.map(flt => {
                    interLinks.push({
                        heading: locationConfig?.nestedLocation?.heading === 'value' ? flt?.name : filter?.heading,
                        filters: nestedLocationData.map(location => ({
                            id: `${filter?.id}_${flt?.id}`,
                            text: filter?.text?.replace('{value}', flt.name)?.replace('{location}', location.name),
                            url: buildURL({ category, location, params: { filter: { [filter.id]: flt.value }}})
                        }))
                    });
                });
            }
        }
    }

    return interLinks?.length > 0
        ? interLinks.map((data, i) => (
            <div className={ css.locationLinkWrapper } data-aut-id="heading" key={ i }>
                { interLinks?.length > 1 ? <span className={ css.footerInterLinkHeaders }>{data?.heading} : </span> : undefined }
                {data?.filters?.map((filter, i) => (
                    <React.Fragment key={ i }>
                        <Link
                            className={ css.footerInterLink }
                            data-aut-id={ filter?.id }
                            to={ filter?.url }
                            title={ filter?.text }
                        >
                            { filter?.text }
                        </Link>
                        {i === data.filters.length - 1 ? undefined : <span> | </span>}
                    </React.Fragment>
                ))}
            </div>
        ))
        : <div />;
});

StructuredFooterInterlinks.displayName = 'StructuredFooterInterlinks';

StructuredFooterInterlinks.propTypes = {
    appliedFilters: PropTypes.arrayOf({
        id: PropTypes.string,
        is_relaxed: PropTypes.bool,
        values: PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            value: PropTypes.string,
            display_order: PropTypes.number
        })
    }),
    category: PropTypes.shape({
        id: PropTypes.string
    }),
    filters: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        description: PropTypes.string,
        values: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            value: PropTypes.string,
            display_order: PropTypes.number
        }))
    })),
    locationDataFromFilter: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        children: PropTypes.array,
        location_id: PropTypes.string,
        location_type: PropTypes.string
    })),
    locationMapping: PropTypes.object,
    selectedLocation: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        parentId: PropTypes.number,
        type: PropTypes.string
    })
};

const swapKeysValues = obj =>
    Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [value, key])
    );

export const mapStateToProps = (state, ownProps) => {
    const category = categorySelector(state, ownProps);
    const itemsMetadata = itemsMetadataSelector(state);
    const filters = itemsMetadata?.filters || [];
    const appliedFilters = itemsMetadata?.applied_filters || [];

    const locationDataFromFilter = filters?.find(filter => filter.id === 'location')?.values;
    let locationMapping = ownProps.marketConfig?.get('locationMapping');

    locationMapping = swapKeysValues(locationMapping);

    const selectedLocation = selectedLocationSelector(state);

    return { appliedFilters, category, filters, locationDataFromFilter, locationMapping, selectedLocation };
};

export default compose(
    withConfig('marketConfig'),
    connect(mapStateToProps)
)(StructuredFooterInterlinks);
